import { LanguagesEnum } from '@/api/enums';
import { phishingContentLanguages } from '@/common/constants/languages';
import { ContentLanguageTabs } from '@/ui/content-language-tabs';
import { TrashIcon } from '@/utils/helpers';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LocalCharacteristic } from './usePhishingCharacteristicsEditor';

type PhishingCharacteristicsManagementTabProps = {
  characteristics: LocalCharacteristic[];
  isLoading: boolean;
  isSaving: boolean;
  onAddRow: () => void;
  onDeleteRow: (rowId: string) => void;
  onChange: (rowId: string, language: LanguagesEnum, value: string) => void;
};

const StyledList = styled(Card)`
  &.p-card {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    box-shadow: none;

    .p-card-body {
      padding: var(--big-padding);
    }

    .p-card-content {
      display: flex;
      flex-direction: column;
      gap: var(--default-padding);
      padding: 0;
    }
  }
`;

const CharacteristicsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--default-padding);
  color: var(--black-main);
`;

const CharacteristicCard = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--small-border-radius);
  padding: var(--medium-big-padding);
`;

const StyledRow = styled(CharacteristicCard)`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: var(--default-padding);
  row-gap: var(--default-padding-small);
`;

const TabsCell = styled.div`
  grid-column: 1 / -1;
`;

const FieldWrapper = styled.div`
  .p-inputtext {
    background-color: var(--white-main);
    border-radius: var(--default-border-radius);
    border: 1px solid var(--gray-dark);
    padding: 13px var(--medium-padding);
  }
`;

const DeleteButton = styled.button`
  all: unset;
  color: var(--black-main);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    path {
      fill: var(--red-main);
    }
    color: var(--red-main);
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const AddRowMuted = styled.span`
  && {
    color: var(--gray-neutral);
  }
`;

const AddRowButton = styled.button<{ disabled?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--semi-xsmall-padding);
  width: 100%;
  padding: var(--small-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  cursor: pointer;
  color: var(--gray-medium-dark);
  font-size: var(--small-font-size);
  background-color: transparent;

  && i.pi {
    color: var(--gray-medium-dark);
  }

  && > span {
    color: var(--gray-medium-dark);
  }
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    border-color: var(--gray-darker);
    color: var(--gray-text);

    && i.pi,
    && > span {
      color: var(--gray-text);
    }
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--big-padding);
`;

export const PhishingCharacteristicsManagementTab: React.FC<
  PhishingCharacteristicsManagementTabProps
> = ({
  characteristics,
  isLoading,
  isSaving,
  onAddRow,
  onDeleteRow,
  onChange,
}) => {
  const { t } = useTranslation();
  const [activeLanguages, setActiveLanguages] = useState<
    Record<string, LanguagesEnum>
  >({});

  if (isLoading) {
    return (
      <LoadingWrapper>
        <ProgressSpinner />
      </LoadingWrapper>
    );
  }

  return (
    <StyledList>
      <CharacteristicsTable>
        {characteristics.map((characteristic) => {
          const activeLanguage =
            activeLanguages[characteristic.rowId] ?? LanguagesEnum.EN;
          const fieldId = `characteristic-${characteristic.rowId}`;

          return (
            <StyledRow key={characteristic.rowId}>
              <TabsCell>
                <ContentLanguageTabs
                  languages={phishingContentLanguages}
                  active={activeLanguage}
                  onChange={(language) =>
                    setActiveLanguages((current) => ({
                      ...current,
                      [characteristic.rowId]: language,
                    }))
                  }
                  isEmpty={(language) =>
                    !characteristic.descriptions[language]?.trim()
                  }
                />
              </TabsCell>
              <FieldWrapper>
                <InputText
                  id={fieldId}
                  value={characteristic.descriptions[activeLanguage] ?? ''}
                  onChange={(e) =>
                    onChange(
                      characteristic.rowId,
                      activeLanguage,
                      e.target.value,
                    )
                  }
                  placeholder={t('globalSettings.characteristicPlaceholder')}
                  className="w-full"
                  aria-label={t('globalSettings.characteristicPlaceholder')}
                />
              </FieldWrapper>
              <DeleteButton
                type="button"
                disabled={isSaving}
                onClick={() => onDeleteRow(characteristic.rowId)}
                aria-label={t('button.delete')}
              >
                <TrashIcon />
              </DeleteButton>
            </StyledRow>
          );
        })}

        <CharacteristicCard>
          <AddRowButton type="button" disabled={isSaving} onClick={onAddRow}>
            <i className="pi pi-plus" />
            <span>
              <Trans
                i18nKey="globalSettings.addCharacteristic"
                components={{ muted: <AddRowMuted /> }}
              />
            </span>
          </AddRowButton>
        </CharacteristicCard>
      </CharacteristicsTable>
    </StyledList>
  );
};
